import {Injectable} from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';

import {Observable, of, throwError} from 'rxjs';
import {catchError, map, switchMap, tap, timeout} from 'rxjs/operators';

import {IRemoteDataService} from '../../interfaces/IRemoteDataService';
import {WebResponse} from '../../models/remote.response/WebResponse';
import {NotificationService} from '../notification/notification.service';
import {AppError} from '../../models/other/app-error.model';
import {CustomError} from '../../models/other/CustomError.model';

import {NewNavigationService} from "@shared/services/new-navigation.service";
import {AuthenticationService} from "@shared/services/authentication/authentication.service";

@Injectable()
export class RemoteDataService implements IRemoteDataService {

    constructor(
        private $httpClient: HttpClient,
        private $msg: NotificationService,
        private newNavigationService: NewNavigationService,
        private authenticationService: AuthenticationService
    ) {
    }

    /**
     * mapper
     */
    private responseMapper(resp: any): WebResponse {

        let response: WebResponse | null;
        response = (<HttpResponse<WebResponse>>resp).body;

        if (!response) {
            return {} as WebResponse;
        }

        // kolejnosc wazna
        if (response && response.Result) {
            response.Statuses = (response.Result.Statuses ? response.Result.Statuses : response.Statuses);
        }

        if (response && response.Result) {
            response.Result = (response.Result.Result ? response.Result.Result : response.Result);
        }


        // opis bledu
        if (response.ErrorCode) {
            switch (response.ErrorCode) {
                //    Ok = 0,
                case 0:
                    response.ErrorDescription = 'Opercja przebiegła pomyślnie';
                    break;
                //    InternalError = 1, // wewnętrzny błąd usługi
                case 1:
                    response.ErrorDescription = 'Wystąpił błąd wewnętrzny serwera';
                    break;
                //    NotValidCredentials = 2, // nieprawidłowe dane uwierzytelniające
                case 2:
                    response.ErrorDescription = 'E-mail lub hasło jest niepoprawne';
                    break;
                //    NoAccessToData = 3, // brak dostępu do danych
                case 3:
                    response.ErrorDescription = 'Brak dostępu do danych';
                    break;
                //    NoSessionOrExpired = 4, //brak sesji lub sesja wygasła
                case 4:
                    response.ErrorDescription = 'Brak sesji lub sesja wygasła';
                    break;
                //    FailToLoadProfileContactData = 5, //nie udało się pobrać danych kontaktowych z xml
                case 5:
                    response.ErrorDescription = 'Nie udało się pobrać danych kontaktowych z xml';
                    break;
                //    DataFail = 6, //nieprawidłowe dane wejściowe
                case 6:
                    response.ErrorDescription = 'Nieprawidłowe dane wejściowe';
                    break;
                //    ResetPasswordTokenIsExpired = 7, //token do resetu hasła wygasł
                case 7:
                    response.ErrorDescription = 'Token do resetu hasła wygasł';
                    break;
                //    ResetPasswordTokenNotExist = 8, //nie znaleziono tokenu do resetu hasła
                case 8:
                    response.ErrorDescription = 'Nie znaleziono tokenu do resetu hasła';
                    break;
                //    OldPasswordIncorrect = 9, //stare hasło jest niepoprawne
                case 9:
                    response.ErrorDescription = 'Błędne hasło';
                    break;
                //    OldPasswordIsEmpty = 10, //stare hasło jest puste
                case 10:
                    response.ErrorDescription = 'Hasło jest puste';
                    break;
                //    ResetPasswordTokenIsEmpty = 11, //token do resetu hasła jest pusty
                case 11:
                    response.ErrorDescription = 'Token do resetu hasła jest pusty';
                    break;
                //    PersonNotFound = 12,
                case 12:
                    response.ErrorDescription = 'Nie znaleziono osoby';
                    break;
                //    SearchEngineNotFound = 13,
                case 13:
                    response.ErrorDescription = 'Nie znaleziono';
                    break;
                //    ClientNotFound = 16, //nie znaleziono klienta
                case 16:
                    response.ErrorDescription = 'Baza danych o tej nazwie nie istnieje';
                    break;
                // inne
                default:
                    response.ErrorDescription = response.ErrorDescription || 'Brak komunikatu';
                    if (resp.ErrorData && resp.ErrorData.status === 0) {
                        response.ErrorDescription = 'Brak połaczenia z serwerem lub podany adres nie został znaleziony';
                    }
                    break;
            }
        }

        return <WebResponse>response;
    }


    private getOpts(opts?: any): any {
        opts = opts || {};
        const defs: any = { notification: true };
        const keys = Object.keys(defs);
        const dkeys = Object.keys(opts);
        for (const k of keys) {
            if (dkeys.indexOf(k) === -1) {
                opts[k] = defs[k];
            }
        }
        return opts;
    }


    /**
     * przygotowanie opcji
     */
    private prepareOptions(opts?: any): Observable<any> {

        return new Observable(observer => {
                // pobranie token
                let xAuthToken = this.authenticationService.sessionId;

                const _headers = <any>{};
                if (xAuthToken) {
                    _headers['SessionId'] = xAuthToken;
                }

                // zbudowanie naglowka
                const headers = new HttpHeaders(_headers);

                // zbudowanie request options
                let opt = <any>{
                    headers: headers,
                    withCredentials: true,
                    observe: "response"
                };

                if(opts && opts['noCredentials'])
                {
                    var cookies = document.cookie.split(";");
                    for (var i = 0; i < cookies.length; i++)
                    {
                        var cookie = cookies[i];
                        var eqPos = cookie.indexOf("=");
                        var name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
                        if(name.indexOf('ROUTEID') >= 0)
                        {
                            document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
                        }
                    }
                }

                observer.next(opt);
                observer.complete();
        });
    }

    httpDelete(url: string, opts?: any): Observable<void> {
        opts = this.getOpts(opts);
        return this.prepareOptions(opts).pipe(switchMap(options => {
          return new Observable<void>(obserever => {
              this.$httpClient.delete(url, options).pipe(
                catchError(err => this.notifyHttpError(err)),
                // .map(response => response.json())
                map(response => this.responseMapper(response)))
                .subscribe(response => {
                  // wystepuje blad
                  if (response.ErrorCode) {
                    if (opts.notification) {
                      this.notifyError(response);
                    }
                    throw throwError(new CustomError(response.ErrorDescription, response.ErrorCode));
                  }
                  obserever.next();
                  obserever.complete();
                }, (err) => obserever.error(err));
            }
          );
        }));
    }

    httpGet(url: string, opts?: any): Observable<any> {
        opts = this.getOpts(opts);
        return this.prepareOptions(opts).pipe(switchMap(options => {
          return this.$httpClient.get(url, options).pipe(
            catchError(err => this.notifyHttpError(err)),
            // .map(response => response.json())
            map(response => this.responseMapper(response)),
            tap(response => {
              // wystepuje blad
              if (response.ErrorCode) {
                if (opts.notification) {
                  this.notifyError(response);
                }
                throw throwError(new CustomError(response.ErrorDescription, response.ErrorCode));
              }
            })
          );
        }));
    }
    /**
     * specyficzna metoda bo nie ma pobrania Array
     */
    httpGetAll(url: string, opts?: any): Observable<any[]> {
        opts = this.getOpts(opts);
        return this.prepareOptions(opts).pipe(switchMap(options => {
            return of([]);
        }));
    }

    httpPost(url: string, obj?: any, opts?: any, timeoutRequest = 60000): Observable<any> {
        opts = this.getOpts(opts);
        return this.prepareOptions(opts).pipe(switchMap(options => {
          return this.$httpClient.post(url, obj, options).pipe(
            timeout(timeoutRequest),
            catchError(err => this.notifyHttpError(err)),
            //.map(response => console.log(response))
            map(response => this.responseMapper(response)),
            tap(response => {
              // wystepuje blad
              if (response.ErrorCode) {
                if (opts.notification) {
                  this.notifyError(response);
                }
                throw throwError(new CustomError(response.ErrorDescription, response.ErrorCode));
              }
            })
          );
        }
        ));
    }

    httpPut(url: string, obj: any, opts?: any): Observable<any> {
        opts = this.getOpts(opts);
        return this.prepareOptions(opts).pipe(switchMap(options => {
          return this.$httpClient.put(url, obj, options).pipe(
            catchError(err => this.notifyHttpError(err)),
            // .map(response => response.json())
            map(response => this.responseMapper(response)),
            tap(response => {
              // wystepuje blad
              if (response.ErrorCode) {
                if (opts.notification) {
                  this.notifyError(response);
                }
                throw throwError(new CustomError(response.ErrorDescription, response.ErrorCode));
              }
            }))
        }));
    }


    /**
     * powiadomienie o bledzie
     */
    private notifyError(response: WebResponse): void {
        //console.error('HTTP ERROR', response); //TODO GB
        const error = <AppError>{
            ErrorCode: response.ErrorCode,
            ErrorDescription: response.ErrorDescription,
            ErrorData: response
        };
        this.$msg.processAppError(error);

        if(response.Status == 500)
          this.newNavigationService.toLogout();
    }

    /**
     * notyfikacja bledow http
     */
    private notifyHttpError(err: any): Observable<Error> {
        // console.error('HTTP ERROR', err.message);//TODO GB

        const error = <AppError>{
            ErrorCode: 500,
            ErrorDescription: err.message,
            ErrorData: err
        };
        this.$msg.processAppError(error);

        console.error(err);

        this.newNavigationService.toLogout();

        const httpError = new CustomError();
        httpError.message = err.message;
        httpError.name = err.name;
        httpError.code = err.status;
        return throwError(httpError);
    }
}
